import { observer } from 'mobx-react';
import { Breadcrumb, Tooltip } from 'antd';
import { HomeOutlined, ShopOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { PathPlacement } from '@/stores/StudioModuleStore';

// Scope-aware breadcrumb from the resolved navigation (StudioModuleStore), so it can
// never disagree with the rail, the launcher or ⌘K:
//   [home] / Merchant (only on pages that show one merchant's data) / Module / Page / subpage
// e.g. Home / Alpine Cardiology / Agent Design / Agents / 42
//      Home / Administration / Models
// A page reached by bookmark whose module is hidden (or that belongs to none) still
// opens — the module crumb says why it is not in the menus.

export const BreadcrumbNav = observer(function BreadcrumbNav() {
  const { merchantContextStore: ctx, studioModuleStore: sm } = useStore();
  const location = useLocation();
  const placement = sm.placementFor(location.pathname);
  const destination = placement.kind === 'unknown' ? undefined : placement.destination;
  const merchant = ctx.selectedMerchant;

  const items: Array<{ title: React.ReactNode; key: string }> = [
    {
      key: 'home',
      title: (
        <Link to="/" aria-label="Home" style={{ color: 'inherit' }}>
          <HomeOutlined />
        </Link>
      ),
    },
  ];

  // Merchant crumb only where the page is about that merchant — a platform page
  // (Models, Audit log…) is the same whichever merchant is selected.
  if (merchant && (!destination || destination.scope !== 'platform')) {
    items.push({
      key: 'merchant',
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ShopOutlined style={{ fontSize: 12, color: colors.slate[400] }} />
          {merchant.title}
        </span>
      ),
    });
  }

  if (destination && placement.kind !== 'home') {
    // No module crumb until the catalog has answered: "not in your navigation" would be a guess while it loads or is down.
    const moduleCrumb = sm.status === 'ready' ? moduleCrumbFor(placement) : null;
    if (moduleCrumb) items.push({ key: 'module', title: moduleCrumb });

    const rest = location.pathname.slice(destination.to.length).split('/').filter(Boolean);
    items.push({
      key: destination.key,
      title: rest.length === 0
        ? <span style={{ fontWeight: 500 }} aria-current="page">{destination.label}</span>
        : <Link to={destination.to} style={{ color: 'inherit' }}>{destination.label}</Link>,
    });
    if (rest.length > 0) {
      // Generic subpage label — page components render their own title above.
      const named = [destination.tabs?.[rest[0]] ?? rest[0], ...rest.slice(1)];
      items.push({ key: rest.join('/'), title: <span style={{ fontWeight: 500 }} aria-current="page">{named.join(' / ')}</span> });
    }
  }

  return (
    <nav aria-label="Breadcrumb">
      <Breadcrumb separator={<span style={{ color: colors.slate[300] }}>/</span>} items={items} style={{ fontSize: 13 }} />
    </nav>
  );
});

function moduleCrumbFor(placement: PathPlacement): React.ReactNode {
  const muted = { color: colors.slate[500] };
  switch (placement.kind) {
    case 'module':
      return <span style={muted}>{placement.module.title}</span>;
    case 'elsewhere': {
      const why =
        placement.info.reason === 'hidden-module'
          ? `${placement.info.moduleTitle ?? 'Its module'} is hidden from navigation. This page still works for you; it just isn't in the menus, search or pins.`
          : placement.info.reason === 'empty-module'
            ? `${placement.info.moduleTitle ?? 'Its module'} has no other pages you can open, so it isn't in the launcher.`
            : 'This page is not in any module yet, so it is not in the menus, search or pins.';
      return (
        <Tooltip title={why}>
          <span style={{ ...muted, display: 'inline-flex', alignItems: 'center', gap: 4 }} aria-label={why}>
            <EyeInvisibleOutlined style={{ fontSize: 12 }} />
            {placement.info.moduleTitle ?? 'Not in navigation'}
          </span>
        </Tooltip>
      );
    }
    case 'not-in-navigation':
      return (
        <Tooltip title="Your navigation doesn't include this page. It opens only if your account may use it.">
          <span style={{ ...muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <EyeInvisibleOutlined style={{ fontSize: 12 }} />
            Not in your navigation
          </span>
        </Tooltip>
      );
    default:
      return null;
  }
}
